import { HttpEvent, HttpEventType, HttpProgressEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { odataParamsFactory } from '@tdw/core';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';
import { DrawingMappingService } from 'src/app/services/drawing-mapping.service';
import { UploadsService } from 'src/app/services/uploads.service';
import { UserProfileService } from 'src/app/services/user-profile.service';
import { toastFactory } from '../../../models';

import { FittingsService } from '../../../services/fittings.service';

import * as fromStore from '../../../store';

@Injectable()
export class AdminEffects {
  removeAssociatedProduct$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.removeAssociatedProduct),
    withLatestFrom(this.store.select(fromStore.selectors.ADMIN.selectCurrentlyEditingFittingId)),
    switchMap(([action, fittingId]) => this.fittingService.removeAssociatedProduct(action.associatedProductId, fittingId || -1)
      .pipe(map(() => fromStore.actions.ADMIN.removeAssociatedProductSuccess()),
        catchError(error => of(fromStore.actions.ADMIN.removeAssociatedProductFail({ error })))))));

  removeDrawing$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.removeDrawing),
    switchMap(action => this.fittingService.saveFitting({ id: action.fittingId, drawing: '' })
      .pipe(map(() => fromStore.actions.ADMIN.saveFittingRecordSuccess()),
        catchError(error => of(fromStore.actions.ADMIN.saveFittingRecordFail({ error })))))));

  uploadDocument$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.uploadDocument),
    switchMap((action) => this.uploadService.uploadFile({ file: action.file, metadata: action.metadata }).pipe(
      map((res: HttpEvent<HttpProgressEvent>) => {
        switch (res.type) {
          case HttpEventType.UploadProgress:
            const progress = Math.round(res.loaded / (res.total || -1) * 100);
            this.store.dispatch(fromStore.actions.ADMIN.reportUploadProgress({ progress }));
            break;
          case HttpEventType.Response:
            setTimeout(() => {
              this.store.dispatch(fromStore.actions.ADMIN.reportUploadProgress({ progress: 0 }));
            }, 800);
        }

        return fromStore.actions.ADMIN.uploadDocumentSuccess();
      }),
      catchError(error => of(fromStore.actions.ADMIN.uploadDocumentFail({ error })))))
  ));

  loadAppInfo$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.loadAppInfo),
    switchMap(() => this.userService.getAppInfo()
      .pipe(map(info => fromStore.actions.ADMIN.loadAppInfoSuccess({ info })),
        catchError(error => of(fromStore.actions.ADMIN.loadAppInfoFail({ error })))))));

  loadDrawingMappings$ = createEffect(() => this.actions$.pipe(
    ofType(
      fromStore.actions.ADMIN.loadDrawingMappings,
      fromStore.actions.ADMIN.changeDrawingMappingsPartNumberFilter,
      fromStore.actions.ADMIN.changeDrawingMappingsfilenameFilter,
      fromStore.actions.ADMIN.patchDrawingMappingSuccess,
      fromStore.actions.ADMIN.addDrawingMappingSuccess
    ),
    withLatestFrom(this.store.select(fromStore.selectors.ADMIN.selectDrawingOdataQuery)),
    switchMap(([, q]) => this.drawingMappingService.get(odataParamsFactory(q))
      .pipe(map(mappings => fromStore.actions.ADMIN.loadDrawingMappingsSuccess({ mappings })),
        catchError(error => of(fromStore.actions.ADMIN.loadDrawingMappingsFail({ error })))))));

  checkFlatterFilesForDrawingMappings$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.loadDrawingMappingsSuccess),
    withLatestFrom(this.store.select(fromStore.selectors.ADMIN.selectDrawingMappingsValue)),
    switchMap(([, vals]) => this.drawingMappingService.getExistenceGraph(vals.map(m => m.filename))
      .pipe(map(result => fromStore.actions.ADMIN.checkFlatterFilesExistenceSuccess({ result })),
        catchError(error => of(fromStore.actions.ADMIN.checkFlatterFilesExistenceFail({ error })))))
  ));

  patchDrawingMapping$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.patchDrawingMapping),
    switchMap(action => this.drawingMappingService.patch(`${action.partNumber},${action.filename}`, action.dwg)
      .pipe(map(() => fromStore.actions.ADMIN.patchDrawingMappingSuccess()), catchError(error => of(fromStore.actions.ADMIN.patchDrawingMappingFail({ error })))))));

  createDrawingMapping$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.addDrawingMapping),
    switchMap(action => this.drawingMappingService.post(action.dwg)
      .pipe(map(() => fromStore.actions.ADMIN.addDrawingMappingSuccess()), catchError(error => of(fromStore.actions.ADMIN.addDrawingMappingFail({ error })))))));

  showToastAfterDrawingSave$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.addDrawingMappingSuccess),
    map(() => fromStore.actions.MESSAGES.addToast({
      toast: toastFactory({
        class: 'success',
        message: 'Drawing Mapping was added',
        title: 'Drawing'
      })
    }))
  ));


  constructor(
    private actions$: Actions,
    private store: Store<fromStore.State>,
    private fittingService: FittingsService,
    private uploadService: UploadsService,
    private userService: UserProfileService,
    private drawingMappingService: DrawingMappingService) { }
}
